/**
 * Client-side rule evaluation engine for Item Customization Templates.
 *
 * Ported from enterprise-commerce/lib/personalization/rule-engine.ts
 *
 * Semantics:
 *   - Case-insensitive matching on all string comparisons
 *   - Each rule evaluated independently (condition -> action)
 *   - Priority-based conflict resolution (higher wins, same -> lower idx)
 *   - Cascading visibility: rules whose when_field is hidden are suppressed.
 *     Uses iterative evaluation (max 5 passes) until visibility stabilizes.
 */

import type {
  CustomizationField,
  CustomizationRule,
  PersonalizationValues,
  RuleAction,
  RuleResult,
} from "./types";

interface TriggeredAction {
  action: RuleAction;
  target_field: string;
  target_value: string;
  priority: number;
  idx: number;
}

/**
 * Evaluate all rules against current field values.
 */
export function evaluateRules(
  fields: CustomizationField[],
  rules: CustomizationRule[],
  currentValues: PersonalizationValues,
): RuleResult {
  const fieldLabels = fields.map((f) => f.label || "");

  // Fields that are targets of "Show Field" rules default to hidden
  const showTargets = new Set<string>();
  if (rules && rules.length > 0) {
    for (const rule of rules) {
      if ((rule.then_action || "Show Field") === "Show Field" && rule.target_field) {
        const matched = findLabelCaseInsensitive(fieldLabels, rule.target_field);
        if (matched) showTargets.add(matched);
      }
    }
  }

  const result: RuleResult = {
    visible: Object.fromEntries(fieldLabels.map((l) => [l, !showTargets.has(l)])),
    forced: Object.fromEntries(fieldLabels.map((l) => [l, null])),
    required: Object.fromEntries(fields.map((f) => [f.label || "", !!f.required])),
  };

  if (!rules || rules.length === 0) return result;

  let prevVisibleKey = "";
  const requiredDefaults = Object.fromEntries(
    fields.map((f) => [f.label || "", !!f.required]),
  );

  for (let pass = 0; pass < 5; pass++) {
    const filterVisible = { ...result.visible };

    // Reset result to defaults before each pass
    for (const l of fieldLabels) {
      result.visible[l] = !showTargets.has(l);
      result.forced[l] = null;
      result.required[l] = requiredDefaults[l] ?? false;
    }

    const triggeredActions: TriggeredAction[] = [];
    for (let i = 0; i < rules.length; i++) {
      const rule = rules[i];
      const whenLabel = findLabelCaseInsensitive(fieldLabels, rule.when_field || "");
      if (whenLabel && filterVisible[whenLabel] === false) continue;

      const fieldVal = getValueCaseInsensitive(currentValues, rule.when_field || "");
      if (checkCondition(rule.operator || "equals", fieldVal, rule.has_value || "")) {
        triggeredActions.push({
          action: (rule.then_action || "Show Field") as RuleAction,
          target_field: rule.target_field || "",
          target_value: rule.target_value || "",
          priority: rule.priority ?? 10,
          idx: rule.idx ?? i,
        });
      }
    }

    const resolved = resolveConflicts(triggeredActions);
    for (const a of resolved) {
      const matched = findLabelCaseInsensitive(fieldLabels, a.target_field);
      if (!matched) continue;

      switch (a.action) {
        case "Show Field":
          result.visible[matched] = true;
          break;
        case "Hide Field":
          result.visible[matched] = false;
          break;
        case "Set Value":
        case "Force Value":
          result.forced[matched] = a.target_value;
          break;
        case "Set Required":
          result.required[matched] = true;
          break;
        case "Clear Required":
          result.required[matched] = false;
          break;
      }
    }

    const visibleKey = JSON.stringify(result.visible);
    if (visibleKey === prevVisibleKey) break;
    prevVisibleKey = visibleKey;
  }

  return result;
}

function checkCondition(
  operator: string,
  fieldValue: string | null | undefined,
  expectedValue: string | null | undefined,
): boolean {
  const fv = fieldValue == null ? "" : String(fieldValue).trim();
  const ev = expectedValue == null ? "" : String(expectedValue).trim();
  const fvLower = fv.toLowerCase();
  const evLower = ev.toLowerCase();

  switch (operator) {
    case "equals":
      return fvLower === evLower;
    case "not_equals":
      return fvLower !== evLower;
    case "is_set":
      return fv.length > 0;
    case "is_empty":
      return fv.length === 0;
    case "contains":
      return fvLower.includes(evLower);
    default:
      return false;
  }
}

function resolveConflicts(actions: TriggeredAction[]): TriggeredAction[] {
  if (actions.length === 0) return [];

  const best: Record<string, TriggeredAction> = {};

  for (const a of actions) {
    const key = `${a.target_field.trim().toLowerCase()}|${actionCategory(a.action)}`;
    const existing = best[key];

    if (!existing) {
      best[key] = a;
    } else if (a.priority > existing.priority) {
      best[key] = a;
    } else if (a.priority === existing.priority && a.idx < existing.idx) {
      best[key] = a;
    }
  }

  return Object.values(best);
}

function actionCategory(action: RuleAction | string): string {
  if (action === "Show Field" || action === "Hide Field") return "visibility";
  if (action === "Set Value" || action === "Force Value") return "value";
  if (action === "Set Required" || action === "Clear Required") return "required";
  return action;
}

function getValueCaseInsensitive(
  values: PersonalizationValues,
  key: string,
): string | null {
  const keyLower = (key || "").trim().toLowerCase();
  for (const [k, v] of Object.entries(values)) {
    if (k.trim().toLowerCase() === keyLower) return v;
  }
  return null;
}

function findLabelCaseInsensitive(labels: string[], target: string): string | null {
  const targetLower = (target || "").trim().toLowerCase();
  for (const label of labels) {
    if (label.trim().toLowerCase() === targetLower) return label;
  }
  return null;
}
